/* Universal app-wide confirm / alert / prompt dialogs.
 *
 * Replaces native window.confirm / window.alert / window.prompt across
 * every portal (doctor, admin, planner, QA, production, COE, patient,
 * sales, support). Built on shadcn's Dialog primitive so it slots into
 * the design system without one-off styling.
 *
 * Usage (imperative — same shape as the native APIs):
 *
 *   import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
 *
 *   if (await confirmDialog({
 *       title: "Delete this lead?",
 *       message: "This will permanently remove the lead from your pipeline.",
 *       confirmText: "Delete",
 *       tone: "danger",
 *   })) {
 *       // user confirmed
 *   }
 *
 *   await alertDialog({ title: "Heads up", message: "Saved successfully." });
 *
 *   const reply = await promptDialog({
 *       title: "New password",
 *       message: "Pick something memorable.",
 *       placeholder: "•••••••",
 *   });
 *
 * Mount `<DialogHost />` exactly once near the app root — it owns the
 * portal that renders the dialog when an imperative call fires.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Warning, Info, Question, ShieldWarning, CheckCircle,
} from "@phosphor-icons/react";

/* ── Tone palette ───────────────────────────────────────────────── */
const TONES = {
    info:    { icon: Info,           bg: "bg-blue-50",   ring: "ring-blue-200",    text: "text-blue-700",    btn: "" },
    success: { icon: CheckCircle,    bg: "bg-emerald-50", ring: "ring-emerald-200", text: "text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700" },
    warn:    { icon: Warning,        bg: "bg-amber-50",   ring: "ring-amber-200",   text: "text-amber-700",   btn: "bg-amber-600 hover:bg-amber-700" },
    danger:  { icon: ShieldWarning,  bg: "bg-rose-50",    ring: "ring-rose-200",    text: "text-rose-700",    btn: "bg-rose-600 hover:bg-rose-700" },
    default: { icon: Question,       bg: "bg-secondary",  ring: "ring-border",      text: "text-foreground",  btn: "" },
};

/* ── Imperative bridge ──────────────────────────────────────────────
 * The DialogHost subscribes to this emitter on mount; the public
 * `confirmDialog / alertDialog / promptDialog` functions fire events
 * that resolve the returned promise once the user picks an action.
 */
const subscribers = new Set();
let pendingRequests = [];

function emit(req) {
    if (subscribers.size === 0) {
        // Host not mounted yet — buffer. Will be drained on subscribe.
        pendingRequests.push(req);
        return;
    }
    subscribers.forEach((fn) => fn(req));
}

/* ── Public API ─────────────────────────────────────────────────── */

/**
 * Modal yes / no confirmation. Returns a Promise resolving to `true`
 * (confirm) or `false` (cancel / dismiss). Always await it.
 */
export function confirmDialog({
    title = "Are you sure?",
    message = "",
    confirmText = "Confirm",
    cancelText = "Cancel",
    tone = "default",
    testid = "confirm-dialog",
} = {}) {
    return new Promise((resolve) => {
        emit({
            kind: "confirm",
            title, message, confirmText, cancelText, tone, testid,
            resolve,
        });
    });
}

/**
 * Modal informational notice. Returns a Promise that resolves when
 * the user dismisses. Resolves to `true` (always) for symmetry.
 */
export function alertDialog({
    title = "Heads up",
    message = "",
    confirmText = "Got it",
    tone = "info",
    testid = "alert-dialog",
} = {}) {
    return new Promise((resolve) => {
        emit({
            kind: "alert",
            title, message, confirmText, tone, testid,
            resolve,
        });
    });
}

/**
 * Modal text-input prompt. Returns a Promise resolving to the entered
 * string OR `null` if the user cancels (mirrors window.prompt's API).
 * Set `multiline: true` for a textarea + optional `minLength` for
 * client-side length enforcement (button stays disabled until met).
 */
export function promptDialog({
    title = "Quick input",
    message = "",
    placeholder = "",
    defaultValue = "",
    confirmText = "OK",
    cancelText = "Cancel",
    tone = "default",
    required = true,
    inputType = "text",
    multiline = false,
    minLength = 0,
    maxLength = 0,
    testid = "prompt-dialog",
} = {}) {
    return new Promise((resolve) => {
        emit({
            kind: "prompt",
            title, message, placeholder, defaultValue, confirmText, cancelText, tone,
            required, inputType, multiline, minLength, maxLength, testid,
            resolve,
        });
    });
}


/* ── Host component (mount once near the app root) ──────────────── */

export function DialogHost() {
    const [active, setActive] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef(null);
    const descriptionId = React.useId();

    // Subscribe to imperative emissions + drain anything buffered before
    // the host mounted (rare but possible during fast hydration).
    useEffect(() => {
        const handler = (req) => setActive((current) => {
            // If something is already open, queue the new request after
            // the user dismisses the current one. Keeps semantics tight
            // (no two dialogs vying for the same modal layer).
            if (current) {
                setTimeout(() => handler(req), 0);
                return current;
            }
            return req;
        });
        subscribers.add(handler);
        const drain = pendingRequests;
        pendingRequests = [];
        drain.forEach(handler);
        return () => { subscribers.delete(handler); };
    }, []);

    // Reset input value whenever a new prompt opens / a non-prompt opens.
    useEffect(() => {
        if (!active) return;
        if (active.kind === "prompt") {
            setInputValue(active.defaultValue || "");
            const t = setTimeout(() => inputRef.current?.focus(), 60);
            return () => clearTimeout(t);
        }
    }, [active]);

    const close = useCallback((result) => {
        if (!active) return;
        active.resolve(result);
        setActive(null);
        setInputValue("");
    }, [active]);

    if (!active) return null;
    const tone = TONES[active.tone] || TONES.default;
    const ToneIcon = tone.icon;
    const isPrompt = active.kind === "prompt";
    const isAlert = active.kind === "alert";
    const isConfirm = active.kind === "confirm";

    const cancelResult = isPrompt ? null : false;
    const confirmResult = isPrompt ? inputValue : true;
    const trimmedLen = (inputValue || "").trim().length;
    const meetsMin = !isPrompt || !active.minLength || trimmedLen >= active.minLength;
    const canSubmit = (!isPrompt || !active.required || trimmedLen > 0) && meetsMin;

    return (
        <Dialog
            open
            onOpenChange={(open) => { if (!open) close(cancelResult); }}
        >
            <DialogContent
                className="max-w-md"
                data-testid={active.testid}
                aria-describedby={descriptionId}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && (isAlert || (isPrompt && !active.multiline)) && canSubmit) {
                        e.preventDefault();
                        close(confirmResult);
                    }
                }}
            >
                <DialogHeader>
                    <div className="flex items-start gap-3">
                        <div className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center ring-1 ${tone.bg} ${tone.text} ${tone.ring}`}>
                            <ToneIcon size={18} weight="duotone" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <DialogTitle className="font-display text-base leading-tight">
                                {active.title}
                            </DialogTitle>
                            {active.message ? (
                                <DialogDescription id={descriptionId} className="mt-1 text-[12.5px] leading-snug text-muted-foreground">
                                    {active.message}
                                </DialogDescription>
                            ) : (
                                <DialogDescription id={descriptionId} className="sr-only">
                                    {active.title}
                                </DialogDescription>
                            )}
                        </div>
                    </div>
                </DialogHeader>
                {isPrompt && (
                    <div className="mt-2">
                        {active.multiline ? (
                            <textarea
                                ref={inputRef}
                                value={inputValue}
                                placeholder={active.placeholder || ""}
                                onChange={(e) => setInputValue(e.target.value)}
                                className="w-full min-h-[96px] rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                                maxLength={active.maxLength || undefined}
                                data-testid={`${active.testid}-input`}
                            />
                        ) : (
                            <Input
                                ref={inputRef}
                                type={active.inputType || "text"}
                                value={inputValue}
                                placeholder={active.placeholder || ""}
                                onChange={(e) => setInputValue(e.target.value)}
                                className="h-10"
                                maxLength={active.maxLength || undefined}
                                data-testid={`${active.testid}-input`}
                            />
                        )}
                        {active.minLength ? (
                            <div className={`mt-1.5 text-[11px] tabular-nums ${meetsMin ? "text-emerald-600" : "text-muted-foreground"}`} data-testid={`${active.testid}-counter`}>
                                {trimmedLen} / min {active.minLength}
                                {active.maxLength ? ` · max ${active.maxLength}` : ""}
                            </div>
                        ) : null}
                    </div>
                )}
                <div className="mt-4 flex justify-end gap-2">
                    {(isConfirm || isPrompt) && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => close(cancelResult)}
                            className="rounded-full"
                            data-testid={`${active.testid}-cancel`}
                        >
                            {active.cancelText || "Cancel"}
                        </Button>
                    )}
                    <Button
                        type="button"
                        onClick={() => close(confirmResult)}
                        disabled={!canSubmit}
                        className={`rounded-full ${tone.btn}`}
                        data-testid={`${active.testid}-confirm`}
                    >
                        {active.confirmText}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
